/**
 * HTTP-запросы через fetch: validateStatus, IRequestSkeleton, ISendRequestResponse, FetchError.
 * Опциональный `timeoutMs` — AbortSignal по истечении времени.
 *
 * @module requests/sendRequest
 */

export type TRequestMethods =
  | 'get'
  | 'GET'
  | 'delete'
  | 'DELETE'
  | 'head'
  | 'HEAD'
  | 'options'
  | 'OPTIONS'
  | 'post'
  | 'POST'
  | 'put'
  | 'PUT'
  | 'patch'
  | 'PATCH'
  | 'purge'
  | 'PURGE'
  | 'link'
  | 'LINK'
  | 'unlink'
  | 'UNLINK';

export interface IRequestSkeleton<TBody = unknown> {
  method: TRequestMethods;
  url: string;
  data?: TBody;
  headers?: Record<string, string> | HeadersInit;
  body?: BodyInit | null;
  /** Если задано и > 0, запрос прерывается по таймауту (AbortController). */
  timeoutMs?: number;
}

export interface ISendRequestResponse<T> {
  data: T;
  headers: Record<string, string>;
  ok: boolean;
  status: number;
  statusText: string;
  url: string;
}

/**
 * Успешный ответ: status в диапазоне 200–299.
 *
 * @param {number} status — HTTP status
 * @returns {boolean}
 */
export function validateStatus(status: number): boolean {
  return status >= 200 && status < 300;
}

/**
 * Ошибка HTTP с распарсенным телом ответа.
 */
export class FetchError extends Error {
  readonly status: number;

  readonly statusText: string;

  readonly headers: Record<string, string>;

  readonly payload: unknown;

  /**
   * @param opts — поля ошибки
   */
  constructor(opts: {
    message: string;
    status: number;
    statusText: string;
    headers: Record<string, string>;
    payload: unknown;
  }) {
    super(opts.message);
    this.name = 'FetchError';
    this.status = opts.status;
    this.statusText = opts.statusText;
    this.headers = opts.headers;
    this.payload = opts.payload;
    Object.setPrototypeOf(this, FetchError.prototype);
  }
}

/**
 * Преобразует Headers в плоский объект.
 *
 * @param {Headers} headers — заголовки ответа
 * @returns {Record<string, string>}
 */
function headersToRecord(headers: Headers): Record<string, string> {
  const out: Record<string, string> = {};
  headers.forEach((value, key) => {
    out[key] = value;
  });
  return out;
}

/**
 * Безопасно парсит тело ответа: JSON либо text.
 *
 * @param {Response} res — объект Response
 * @returns {Promise<unknown>}
 */
async function parseBodySafe(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return await res.text();
  }
}

/**
 * Выполняет HTTP-запрос; при неуспешном статусе выбрасывает FetchError.
 *
 * @template T — ожидаемый тип тела при успехе
 * @param {IRequestSkeleton} requestSkeleton — параметры запроса
 * @returns {Promise<ISendRequestResponse<T>>}
 */
export async function sendRequest<T = unknown>(requestSkeleton: IRequestSkeleton): Promise<ISendRequestResponse<T>> {
  if (
    requestSkeleton.method === null ||
    requestSkeleton.method === undefined ||
    typeof requestSkeleton.method !== 'string'
  ) {
    throw new Error('sendRequest: "method" is required and must be a string');
  }
  if (requestSkeleton.url === null || requestSkeleton.url === undefined || typeof requestSkeleton.url !== 'string') {
    throw new Error('sendRequest: "url" is required and must be a string');
  }

  const methodUpper = requestSkeleton.method.toUpperCase();
  const method = methodUpper as TRequestMethods;
  const url = requestSkeleton.url;

  let body: BodyInit | null | undefined = requestSkeleton.body;
  if (body === undefined && requestSkeleton.data !== undefined) {
    body = JSON.stringify(requestSkeleton.data);
  }

  const headers: HeadersInit = requestSkeleton.headers ?? { 'Content-Type': 'application/json' };

  let abortController: AbortController | undefined;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutMs = requestSkeleton.timeoutMs;
  if (timeoutMs !== undefined && timeoutMs > 0) {
    abortController = new AbortController();
    timeoutId = setTimeout(() => {
      abortController?.abort();
    }, timeoutMs);
  }

  const init: RequestInit = { method, headers };
  if (body !== null && body !== undefined) {
    init.body = body;
  }
  if (abortController !== undefined) {
    init.signal = abortController.signal;
  }

  try {
    const response = await fetch(url, init);
    const status = response.status;
    const statusText = response.statusText;
    const headersRecord = headersToRecord(response.headers);

    if (!validateStatus(status)) {
      const payload = await parseBodySafe(response);
      throw new FetchError({
        message: `HTTP ${status} ${statusText}`,
        status,
        statusText,
        headers: headersRecord,
        payload,
      });
    }

    const parsed = await parseBodySafe(response);
    return {
      ok: response.ok,
      url: response.url,
      status,
      statusText,
      headers: headersRecord,
      data: parsed as T,
    };
  } catch (error) {
    if (error instanceof FetchError) {
      throw error;
    }
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error(`[sendRequest] Request aborted or timed out (${url})`);
    }
    if (error !== null && typeof error === 'object' && 'status' in error) {
      const e = error as Record<string, unknown>;
      const st = e.status;
      const stText = e.statusText;
      const msg = e.message;
      throw new FetchError({
        message: typeof msg === 'string' ? msg : `Request failed with status ${String(st)}`,
        status: typeof st === 'number' ? st : 0,
        statusText: typeof stText === 'string' ? stText : '',
        headers: {},
        payload: error,
      });
    }
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(`[sendRequest] Unexpected error: ${message}`);
  } finally {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
  }
}
